
import { motion, type MotionValue } from "framer-motion";
import { KEYFRAME_BANDS } from "./keyframe-data";
import { Beat1Unmapped } from "./beats/beat1-unmapped";
import { Beat2Scan } from "./beats/beat2-scan";
import { Beat3Graph } from "./beats/beat3-graph";
import { Beat4Workload } from "./beats/beat4-workload";
import { Beat5Drift } from "./beats/beat5-drift";
import { Beat6Outputs, useDimAtBeat5Plus } from "./beats/beat6-outputs";
import { useGraphScene } from "./graph-scene";
import { ModalLayer } from "./elements/modal-layer";

interface Props {
  progress: MotionValue<number>;
  hydrated: boolean;
}

// KEYFRAME_BANDS has exactly 6 entries (indices 0–5); all accesses below are in-bounds.
const [kb0, kb1, kb2, kb3, kb4, kb5] = KEYFRAME_BANDS as [
  typeof KEYFRAME_BANDS[number],
  typeof KEYFRAME_BANDS[number],
  typeof KEYFRAME_BANDS[number],
  typeof KEYFRAME_BANDS[number],
  typeof KEYFRAME_BANDS[number],
  typeof KEYFRAME_BANDS[number],
];

export function MotionModeCanvas({ progress, hydrated }: Props) {
  const dim = useDimAtBeat5Plus(progress, kb4);
  const scene = useGraphScene(progress, KEYFRAME_BANDS);

  // Hook runs before the guard so it is called unconditionally (Rules of Hooks).
  if (!hydrated) {
    return null;
  }

  return (
    <>
      <Beat1Unmapped
        progress={progress}
        band={kb0}
        nextBand={kb1}
        bandAfterNext={kb2}
      />

      {/* Dim cascade: assembled graph + beat 4 dim once beat 5 mid is crossed */}
      <motion.g style={{ opacity: dim }}>
        <Beat3Graph progress={progress} band={kb2} />
        <Beat4Workload
          progress={progress}
          band={kb3}
          nextBand={kb4}
        />
      </motion.g>

      <Beat5Drift
        progress={progress}
        band={kb4}
      />
      <Beat6Outputs progress={progress} band={kb5} />
      <ModalLayer modal={scene.modal} x={40} y={36} w={320} />

      {/* Scan sweep renders on top during beat 2. */}
      <Beat2Scan progress={progress} band={kb1} />
    </>
  );
}
